import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { translateFn } from '../../../utils/translator.helper';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { Rights } from '@nirvana/shared/constants';
import { GzTextWebComponent } from '../../../ui/gzText';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { WacPagesRecord } from '../../webAccessControlCustomPagesApp/types';
import { UrlValidator } from '../../../ui/gzForm/validators/url.validator';
import { GzMarkdownEditorWebComponent } from '../../../ui/gzMarkdownEditor';
import { TemplateState } from '../../../types/common/types';

export class WebAccessControlCustomPagesFormView extends BaseWebComponentView {
  private isFormEnabled: boolean;
  private customPage: WacPagesRecord;
  private customPagesFormGroup: FormGroup;
  private customPagesFormGroupSubscription: Subscription | null;

  public dom: {
    $hostElement: HTMLElement;
    $footerMessage: GzTextWebComponent;
    $btnCancel: GzButtonWebComponent;
    $btnSave: GzButtonWebComponent;
    $pageTitle: GzTitleWebComponent;
    $warningPageTitle: GzTitleWebComponent;
    $blockPageTitle: GzTitleWebComponent;
    $pageNameFieldForm: GzFormFieldWebComponent;
    $btnToggleSwitchAllowChange: GzFormFieldWebComponent;
    $logoLinkFieldForm: GzFormFieldWebComponent;
    $warningPageFormField: GzFormFieldWebComponent;
    $blockPageFormField: GzFormFieldWebComponent;
    $wrapper: HTMLElement;
    $loadingErrorMessage: HTMLElement;
  };

  public static getDefaultCustomPage(): WacPagesRecord {
    return {
      name: '',
      logoLink: '',
      warningPage: '',
      blockPage: '',
      company: {
        id: Application.Main.getActiveCompany().id,
        name: Application.Main.getActiveCompany().name,
      },
      allowChangeByOtherUsers: false,
    };
  }

  public static hasCurrentUserPermissions(record: WacPagesRecord): boolean {
    const hasCurrentUserPermissions =
      !record.created?.userId || record.created?.userId === Application.Main.user._id || record.allowChangeByOtherUsers;
    return Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]) && hasCurrentUserPermissions;
  }

  public getDomElements(): void {
    this.dom.$hostElement = this.shadow;
    this.dom.$pageTitle = this.shadow.querySelector('#pageTitle');
    this.dom.$warningPageTitle = this.shadow.querySelector('#warningPageTitle');
    this.dom.$blockPageTitle = this.shadow.querySelector('#blockPageTitle');
    this.dom.$btnCancel = this.shadow.querySelector('#btnCancel');
    this.dom.$btnSave = this.shadow.querySelector('#btnSave');
    this.dom.$pageNameFieldForm = this.shadow.querySelector('#pageNameFieldForm');
    this.dom.$btnToggleSwitchAllowChange = this.shadow.querySelector('#btnToggleSwitchAllowChange');
    this.dom.$logoLinkFieldForm = this.shadow.querySelector('#logoLinkFieldForm');
    this.dom.$footerMessage = this.shadow.querySelector('#footerMessage');
    this.dom.$warningPageFormField = this.shadow.querySelector('#warningPageFormField');
    this.dom.$blockPageFormField = this.shadow.querySelector('#blockPageFormField');
    this.dom.$loadingErrorMessage = this.shadow.querySelector('.loading-error--message');
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');

    (this.dom.$warningPageFormField.Input as GzMarkdownEditorWebComponent).initialContent = '';
    (this.dom.$blockPageFormField.Input as GzMarkdownEditorWebComponent).initialContent = '';
  }

  async buildSuccessView(data: { customPage: WacPagesRecord | undefined }) {
    this.customPage = data.customPage ?? WebAccessControlCustomPagesFormView.getDefaultCustomPage();
    this.isFormEnabled = WebAccessControlCustomPagesFormView.hasCurrentUserPermissions(this.customPage);

    this.unsubscribeObservers();
    this.setTranslations();
    this.createCustomPageFormGroup();
    this.setDisabledPropertyToFormFields();
    this.resetCustomPageFormFields();
    this.removeLoadingState();
  }

  private createCustomPageFormGroup() {
    this.dom.$pageNameFieldForm.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$blockPageFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$warningPageFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$logoLinkFieldForm.Validators = [UrlValidator(translateFn('Enter a valid URL'))];

    this.customPagesFormGroup = new FormGroup({
      name: this.dom.$pageNameFieldForm,
      logoLink: this.dom.$logoLinkFieldForm,
      warningPage: this.dom.$warningPageFormField,
      blockPage: this.dom.$blockPageFormField,
      allowChangeByOtherUsers: this.dom.$btnToggleSwitchAllowChange,
    });

    if (this.isFormEnabled) {
      this.customPagesFormGroupSubscription = this.customPagesFormGroup.valid$.subscribe({
        next: valid => {
          if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
            this.dom.$btnSave.disabled = true;
          } else {
            this.dom.$btnSave.disabled = !valid;
          }
        },
      });
    }
  }

  private setDisabledPropertyToFormFields() {
    if (this.isFormEnabled) {
      this.setDisabledComponents(false);
    } else {
      this.setDisabledComponents(true);
    }

    if (!this.customPage.created?.userId || this.customPage.created?.userId === Application.Main.user._id) {
      elementClassListRemove(this.dom.$btnToggleSwitchAllowChange, ElementClass.HIDDEN);
      elementClassListAdd(this.dom.$footerMessage, ElementClass.HIDDEN);
    } else {
      elementClassListAdd(this.dom.$btnToggleSwitchAllowChange, ElementClass.HIDDEN);

      if (this.customPage.allowChangeByOtherUsers) {
        elementClassListAdd(this.dom.$footerMessage, ElementClass.HIDDEN);
      } else {
        elementClassListRemove(this.dom.$footerMessage, ElementClass.HIDDEN);
      }
    }

    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.dom.$btnSave.disabled = true;
      this.dom.$footerMessage.textContent = translateFn('Additional rights required to save this page.');
      elementClassListRemove(this.dom.$footerMessage, ElementClass.HIDDEN);
    }
  }

  private setDisabledComponents(disable: boolean) {
    this.isFormEnabled = disable;
    this.customPagesFormGroup.setDisabled(disable);
  }

  public resetCustomPageFormFields() {
    this.setCustomPageFormData(this.customPage);
    this.dom.$pageNameFieldForm.resetField();
    this.dom.$logoLinkFieldForm.resetField();
    this.dom.$warningPageFormField.resetField();
    this.dom.$blockPageFormField.resetField();
  }

  public setCustomPageFormData(customPage: WacPagesRecord) {
    this.customPagesFormGroup.value = {
      name: customPage.name,
      logoLink: customPage.logoLink,
      warningPage: customPage.warningPage,
      blockPage: customPage.blockPage,
      allowChangeByOtherUsers: customPage.allowChangeByOtherUsers,
    };
  }

  public getFormData(): WacPagesRecord {
    return Object.assign(this.customPage, this.customPagesFormGroup.value);
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  public buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private unsubscribeObservers() {
    this.customPagesFormGroup?.unsubscribe();
    this.customPagesFormGroupSubscription?.unsubscribe();
  }

  private setTranslations(): void {
    this.dom.$pageTitle.textContent = this.customPage._id ? translateFn('Edit page') : translateFn('Add page');
    this.dom.$warningPageTitle.textContent = translateFn('Warning page');
    this.dom.$blockPageTitle.textContent = translateFn('Block page');
    this.dom.$btnCancel.textContent = translateFn('Cancel');
    this.dom.$btnSave.textContent = translateFn('Save#preserve state');
    this.dom.$pageNameFieldForm.label = translateFn('Page name');
    this.dom.$logoLinkFieldForm.label = translateFn('Logo link');
    this.dom.$btnToggleSwitchAllowChange.Input.innerText = translateFn('Allow other users to make updates');
    this.dom.$footerMessage.textContent = translateFn('Cannot save this page because it belongs to another company.');
    this.dom.$loadingErrorMessage.innerHTML = this.commonStrings.generalSystemError;
  }
}
